import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { findRole } from "@/coquille-bridge";
import { KarateUserAvatar } from "@/components/avatar/KarateUserAvatar.jsx";
import { KarateAvatarPhotoModal } from "@/components/avatar/KarateAvatarPhotoModal.jsx";
import {
  KARATE_AVATAR_EVENT,
  resolveAvatarUrlForPerson,
  resolveBackendAvatarUrl,
  syncProfileAvatarFromBackend,
} from "@/services/karateAvatarStorage.js";
import { getStoredBackendUser } from "@/api/karateBackendApi.js";
import { isHybridDataMode } from "@/config/backendStaging049.js";
import { karateAppConfig } from "@/config/karateAppConfig.js";
import { getKarateUi } from "@/i18n/karateUiI18n.js";

/**
 * Menu compte GSD — profil, mot de passe, changement de mode, déconnexion.
 * @param {{
 *   displayName?: string,
 *   email?: string|null,
 *   personKey?: string|null,
 *   locale?: 'fr'|'en',
 *   activeRoleUi?: string,
 *   switchableRoles?: Array<{ id: string, label: string, icon?: string }>,
 *   canSwitchRole?: boolean,
 *   canChangePassword?: boolean,
 *   onRoleChange?: (roleId: string) => void,
 *   onLogout?: () => void,
 *   testId?: string,
 * }} props
 */
export function KarateHeaderProfileMenu({
  displayName,
  email,
  personKey,
  locale = "fr",
  activeRoleUi = "hanshi",
  switchableRoles = [],
  canSwitchRole = false,
  canChangePassword = false,
  onRoleChange,
  onLogout,
  testId = "karate-header-profile",
}) {
  const navigate = useNavigate();
  const [menuOpen, setMenuOpen] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [photoUrl, setPhotoUrl] = useState(null);
  const labels = useMemo(() => {
    const ui = getKarateUi(locale);
    return {
      ...ui.headerProfile,
      ...ui.accountMenu,
      ...ui.avatarManagement,
    };
  }, [locale]);

  const resolvedKey = personKey || displayName || "user";
  const roleMeta = findRole(karateAppConfig, activeRoleUi, locale);
  const activeRoleLabel = roleMeta?.label ?? activeRoleUi;

  const refreshPhoto = useCallback(async () => {
    const backendUser = getStoredBackendUser();
    const personId = backendUser?.personId || backendUser?.person?.id || resolvedKey;

    if (isHybridDataMode()) {
      const synced = await syncProfileAvatarFromBackend().catch(() => null);
      if (synced) {
        setPhotoUrl(synced);
        return;
      }
      const backendPath = backendUser?.person?.avatarUrl || backendUser?.person?.avatar_url || null;
      if (backendPath) {
        setPhotoUrl(resolveBackendAvatarUrl(backendPath));
        return;
      }
      setPhotoUrl(null);
      return;
    }

    const backendPath = backendUser?.person?.avatarUrl || backendUser?.person?.avatar_url || null;
    setPhotoUrl(resolveAvatarUrlForPerson(personId, backendPath, { allowLocalFallback: true }));
  }, [resolvedKey]);

  useEffect(() => {
    refreshPhoto();
    const onUpdate = () => refreshPhoto();
    window.addEventListener(KARATE_AVATAR_EVENT, onUpdate);
    window.addEventListener("karate-backend-auth-updated", onUpdate);
    return () => {
      window.removeEventListener(KARATE_AVATAR_EVENT, onUpdate);
      window.removeEventListener("karate-backend-auth-updated", onUpdate);
    };
  }, [refreshPhoto]);

  useEffect(() => {
    if (!menuOpen) return undefined;
    const onDoc = (e) => {
      if (!e.target.closest(`[data-testid="${testId}"]`)) setMenuOpen(false);
    };
    const onKey = (e) => {
      if (e.key === "Escape") setMenuOpen(false);
    };
    document.addEventListener("click", onDoc);
    window.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("click", onDoc);
      window.removeEventListener("keydown", onKey);
    };
  }, [menuOpen, testId]);

  useEffect(() => {
    if (!modalOpen) return undefined;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = prev;
    };
  }, [modalOpen]);

  const closeMenu = () => setMenuOpen(false);

  const handleRolePick = (roleId) => {
    if (roleId === activeRoleUi) {
      closeMenu();
      return;
    }
    onRoleChange?.(roleId);
    closeMenu();
    navigate(roleId === "hanshi" ? "/admin" : roleId === "sensei" ? "/sensei" : roleId === "assistant" ? "/sempai" : roleId === "parent" ? "/parent" : "/mon-espace");
  };

  return (
    <div className="karate-header-profile" data-testid={testId}>
      <button
        type="button"
        className="karate-account-menu-trigger"
        data-testid="karate-header-account-trigger"
        aria-expanded={menuOpen}
        aria-haspopup="menu"
        onClick={() => setMenuOpen((v) => !v)}
      >
        <KarateUserAvatar displayName={displayName} photoUrl={photoUrl} size="sm" testId="karate-header-avatar" />
        <span className="karate-account-menu-trigger__name">{displayName}</span>
        <span className="karate-account-menu-trigger__chevron" aria-hidden>
          ▾
        </span>
      </button>
      <span className="sr-only" data-testid="karate-header-avatar-source">
        {photoUrl ? "backend" : "initials"}
      </span>

      {menuOpen ? (
        <div className="karate-header-profile__menu karate-account-menu" data-testid="karate-header-profile-menu" role="menu">
          <div className="karate-account-menu__identity" data-testid="karate-account-identity">
            <KarateUserAvatar displayName={displayName} photoUrl={photoUrl} size="md" testId="karate-account-menu-avatar" />
            <div className="karate-account-menu__meta">
              <p className="karate-account-menu__name">{displayName}</p>
              {email ? <p className="karate-account-menu__email">{email}</p> : null}
            </div>
          </div>

          <Link
            to="/mes-informations"
            className="karate-header-profile__item"
            role="menuitem"
            data-testid="karate-account-link-profile"
            onClick={closeMenu}
          >
            {labels.myProfile}
          </Link>

          {canChangePassword ? (
            <Link
              to="/changer-mot-de-passe"
              className="karate-header-profile__item"
              role="menuitem"
              data-testid="karate-account-link-change-password"
              onClick={closeMenu}
            >
              {labels.changePassword}
            </Link>
          ) : null}

          {canSwitchRole ? (
            <div className="karate-account-menu__section" data-testid="karate-account-role-section">
              <p className="karate-account-menu__section-label">{labels.useAppAs}</p>
              <p className="karate-account-menu__mode-current" data-testid="karate-account-current-mode">
                {labels.currentMode}: {activeRoleLabel}
              </p>
              <div className="karate-account-menu__roles" data-testid="gsd-role-switcher">
                {switchableRoles.map((role) => (
                  <button
                    key={role.id}
                    type="button"
                    className={`karate-account-menu__role${role.id === activeRoleUi ? " karate-account-menu__role--active" : ""}`}
                    role="menuitem"
                    data-testid={`gsd-role-switch-${role.id}`}
                    aria-current={role.id === activeRoleUi ? "true" : undefined}
                    onClick={() => handleRolePick(role.id)}
                  >
                    {role.icon ? `${role.icon} ` : ""}
                    {role.label}
                  </button>
                ))}
              </div>
            </div>
          ) : (
            <p className="karate-account-menu__mode-current" data-testid="karate-account-current-mode">
              {labels.currentMode}: {activeRoleLabel}
            </p>
          )}

          <div className="karate-account-menu__divider" aria-hidden />

          <Link
            to="/"
            className="karate-header-profile__item"
            role="menuitem"
            data-testid="karate-account-link-public-site"
            onClick={closeMenu}
          >
            {labels.viewPublicSite}
          </Link>

          <button
            type="button"
            className="karate-header-profile__item karate-account-menu__logout"
            role="menuitem"
            data-testid="gsd-logout-btn"
            onClick={() => {
              closeMenu();
              void Promise.resolve(onLogout?.());
            }}
          >
            {labels.logout}
          </button>
        </div>
      ) : null}

      <KarateAvatarPhotoModal
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        onSaved={async (url) => {
          setPhotoUrl(url);
          await refreshPhoto();
          window.dispatchEvent(new Event(KARATE_AVATAR_EVENT));
        }}
        personKey={resolvedKey}
        locale={locale}
        labels={labels}
        currentPhotoUrl={photoUrl}
      />
    </div>
  );
}
